import {
  decodeToken,
  getRefreshToken,
  getTokens,
  isTokenExpired,
  removeTokens,
  setTokens,
} from '@discover-frontend/auth';
import { FUSION_TYPES, PRODUCT_SLUGS } from '@discover-frontend/constants';
import {
  getExtendedProductScopeUsOrGlobal,
  getProductSlug,
} from '@discover-frontend/utils';

const getDecodedToken = (token) => {
  const decodedToken = decodeToken(token);

  if (!decodedToken) return null;

  try {
    return {
      ...decodedToken,
      products: JSON.parse(decodedToken.products),
    };
  } catch (error) {
    console.warn(error);
    return null;
  }
};

const getDecodedIdToken = () => {
  const { idToken } = getTokens();
  return getDecodedToken(idToken);
};

const getIdTokenClaim = (key) => () => getDecodedIdToken()?.[key];

const checkTokenExpired = () => {
  const decodedToken = getDecodedIdToken();

  if (!decodedToken) return false;

  return isTokenExpired(decodedToken.exp);
};

const isLogged = () => {
  const { accessToken } = getTokens();
  const decodedToken = getDecodedToken(accessToken);

  if (!decodedToken) return false;

  return !isTokenExpired(decodedToken.exp);
};

const isAvailableQueryBuilderType = (queryBuilderType) => {
  const decodedToken = getDecodedIdToken();
  const scope = getExtendedProductScopeUsOrGlobal();
  const product = decodedToken && decodedToken.products[scope];
  return (
    product &&
    product.audienceBuilders?.includes(queryBuilderType.toLowerCase())
  );
};

const isAvailableAddon = (addon) => {
  const decodedToken = getDecodedIdToken();
  const scope = getExtendedProductScopeUsOrGlobal();

  const isAnalyze = addon === 'analyze';
  const product = decodedToken && decodedToken.products[scope];
  const hasAddon = product && product.addons?.includes(addon);
  return isAnalyze || hasAddon;
};

const isInternalUser = () => getDecodedIdToken()?.isInternalUser === 'true';

const requestSingleSignOn = () => {
  window.location.href = `${
    process.env.REACT_APP_ID_SERVER_AUTH
  }?app_id=${getProductSlug()}&redirectUrl=${encodeURIComponent(
    window.location.href
  )}`;
};

const requestSingleSignOff = () => {
  window.location.href = `${
    process.env.REACT_APP_ID_SERVER_BASE
  }/logout?app_id=${getProductSlug()}&redirectUrl=${encodeURIComponent(
    window.location.origin
  )}`;
};

const getCurrentProductByScope = () => {
  const decodedToken = getDecodedIdToken();
  const scope = getExtendedProductScopeUsOrGlobal();
  return decodedToken?.products?.[scope] || null;
};

export const hasSpecificFusion = (fusionType) => {
  const product = getCurrentProductByScope();
  return Boolean(product?.fusions?.includes(fusionType));
};

const hasTgiFusion = () => hasSpecificFusion(FUSION_TYPES.TGI);
const hasMriSimmonsFusion = () => hasSpecificFusion(FUSION_TYPES.MRI_SIMMONS);
const hasGwiFusion = () => hasSpecificFusion(FUSION_TYPES.GWI);
const hasWWGwiMediaFusion = () => hasSpecificFusion(FUSION_TYPES.WW_GWI_MED);

const getSources = (dataSource) => {
  if (dataSource !== 'twitter') return dataSource;

  const fusions = ['twitter'];

  Object.values(FUSION_TYPES).forEach((fusion) => {
    if (hasSpecificFusion(fusion)) fusions.push(fusion);
  });

  return fusions.join(',');
};

const getMyId = getIdTokenClaim('userId');
const getMyEmail = getIdTokenClaim('email');
const getCompanyIdFromToken = getIdTokenClaim('companyId');
const getCompanyNameFromToken = getIdTokenClaim('companyName');

const getUserInfoFromToken = (user) => {
  const decodedToken = getDecodedIdToken();
  const scope = getExtendedProductScopeUsOrGlobal();
  const product =
    decodedToken && decodedToken.products && decodedToken.products[scope];

  return {
    username: user.email,
    email: user.email,
    firstname: user.name,
    lastname: user.surname,
    signUpAt: decodedToken && new Date(decodedToken.iat * 1000),
    role: isTrialUser() ? 'trial' : decodedToken?.scopes,
    props: {
      companyName: decodedToken?.companyName,
      productId: scope,
      fusions: product?.fusions?.join(', ') || '',
      addons: product?.addons?.join(', ') || '',
      audienceBuilders: product?.audienceBuilders?.join(', ') || '',
    },
  };
};

const isCsm = () => {
  const decodedToken = getDecodedIdToken();

  return (
    (decodedToken && decodedToken.products[PRODUCT_SLUGS.HELIXA_CSM]) || false
  );
};

export const isTrialUser = () => {
  const decodedToken = getDecodedIdToken();

  return decodedToken != null && decodedToken.trial != null;
};

export {
  checkTokenExpired,
  getCompanyIdFromToken,
  getCompanyNameFromToken,
  getMyEmail,
  getMyId,
  getRefreshToken,
  getSources,
  getTokens,
  getUserInfoFromToken,
  hasGwiFusion,
  hasMriSimmonsFusion,
  hasTgiFusion,
  hasWWGwiMediaFusion,
  isAvailableAddon,
  isAvailableQueryBuilderType,
  isCsm,
  isInternalUser,
  isLogged,
  removeTokens,
  requestSingleSignOff,
  requestSingleSignOn,
  setTokens,
};
